// The scrolling client strip (.marquee3k, partials/_simpleHeaderMarquee.twig).
//
// A port of the vendored marquee3k plugin with the same markup, class names
// and arithmetic, so the Sass is untouched, and with a teardown the plugin
// never had: the plugin kept one global list of marquees and one animation
// frame loop for the life of the document, which is wrong once pages come and
// go without a reload. Each mount here owns its marquees, its frame loop and
// its resize listener, and gives them all back on unmount.
//
// How a marquee works: the element's first child is the content; it is
// wrapped, cloned enough times to fill the parent plus one, and the wrapper
// slides left by `data-speed` pixels a frame (right with `data-reverse`),
// snapping back by one content width when a full copy has passed, so the
// movement reads as endless. `data-pausable` pauses it under the pointer.

const SELECTOR = "marquee3k";
const RESIZE_SETTLE = 250; // ms of quiet after a resize before clones are topped up

class Marquee {
  constructor(element) {
    this.element = element;
    this.speed = Number(element.dataset.speed) || 0.25;
    this.pausable = element.dataset.pausable !== undefined;
    this.reverse = element.dataset.reverse !== undefined;
    this.paused = false;
    this.offset = 0;

    this.content = element.children[0];
    this.wrapper = document.createElement("div");
    this.wrapper.classList.add(`${SELECTOR}__wrapper`);
    this.wrapper.style.whiteSpace = "nowrap";

    this.content.classList.add(`${SELECTOR}__copy`);
    this.content.style.display = "inline-block";
    this.contentWidth = this.content.offsetWidth;
    const parentWidth = element.parentElement.getBoundingClientRect().width;
    const copies = this.contentWidth > parentWidth ? 2 : Math.ceil((parentWidth - this.contentWidth) / this.contentWidth) + 1;
    this.wrapper.append(this.content);
    for (let i = 0; i < copies; i++) this.addCopy();
    if (this.reverse) this.offset = -this.contentWidth;
    element.append(this.wrapper);
    element.classList.add("is-init");
  }

  addCopy() {
    const copy = this.content.cloneNode(true);
    copy.style.display = "inline-block";
    copy.classList.add(`${SELECTOR}__copy`);
    this.wrapper.append(copy);
  }

  // One frame: slide by `speed`, snapping back once a whole copy has passed.
  step() {
    if (this.paused) return;
    const withinCopy = this.reverse ? this.offset < 0 : this.offset > -this.contentWidth;
    if (withinCopy) this.offset -= this.speed * (this.reverse ? -1 : 1);
    else this.offset = this.reverse ? -this.contentWidth : 0;
    this.wrapper.style.transform = `translate(${this.offset}px, 0) translateZ(0)`;
  }

  // After the window has grown, enough copies to fill the new width.
  refill(grownBy) {
    this.contentWidth = this.content.offsetWidth;
    const copies = Math.ceil(grownBy / this.contentWidth) + 1;
    for (let i = 0; i < copies; i++) this.addCopy();
  }
}

export function mountMarquees({ root, signal }) {
  const marquees = [...root.querySelectorAll(`.${SELECTOR}`)].map((element) => new Marquee(element));
  if (!marquees.length) return;

  let frame = requestAnimationFrame(function tick() {
    marquees.forEach((marquee) => marquee.step());
    frame = requestAnimationFrame(tick);
  });

  marquees.forEach(({ element, pausable }) => {
    if (!pausable) return;
    element.addEventListener("mouseenter", () => (marqueeOf(element).paused = true), { signal });
    element.addEventListener("mouseleave", () => (marqueeOf(element).paused = false), { signal });
  });
  const marqueeOf = (element) => marquees.find((marquee) => marquee.element === element);

  let lastWidth = window.innerWidth;
  let settle;
  window.addEventListener(
    "resize",
    () => {
      clearTimeout(settle);
      settle = setTimeout(() => {
        const grownBy = window.innerWidth - lastWidth;
        if (grownBy > 0) marquees.forEach((marquee) => marquee.refill(grownBy));
        lastWidth = window.innerWidth;
      }, RESIZE_SETTLE);
    },
    { signal },
  );

  return () => {
    cancelAnimationFrame(frame);
    clearTimeout(settle);
  };
}
